import * as React from 'react'
import styled from 'styled-components'

import { t, tc } from '@owl-nest/localize'
import * as plume from '@ulule/owl-kit-components/next'

import { BCorp, Pop, SimpleDashedLine, SimpleDashedLineXS } from './Vectors'

export type FigureItem = {
  title: string
  image: string
}

export function Figures(): React.ReactElement {
  return (
    <FiguresWrapper>
      <plume.styles.heading.L asDesktop="XL">
        <Pop />
        {t('Ulule is…')}
      </plume.styles.heading.L>
      <GridFigures>
        {figureItems.map((item, index) => (
          <Figure key={index}>
            <plume.Pastille size="small">
              <img src={item.image} alt="" />
            </plume.Pastille>
            <plume.styles.copy.M asDesktop="L">{item.title()}</plume.styles.copy.M>
            <FigureLine>
              <SimpleDashedLine />
              <SimpleDashedLineXS />
            </FigureLine>
          </Figure>
        ))}
      </GridFigures>
      <Annotation>
        <BCorp />
        <plume.styles.copy.S asDesktop="M">
          {t('Ulule is a certified B Corp, a pioneer in positive impact.')}
        </plume.styles.copy.S>
      </Annotation>
    </FiguresWrapper>
  )
}

const figureItems = [
  {
    image: `${process.env.ASSET_PREFIX}/assets/img/discover/team_ellipse.png`,
    title: () => tc('A team of [strong: 50 passionate and committed people]', { strong: <strong /> }),
  },
  {
    image: `${process.env.ASSET_PREFIX}/assets/img/discover/experts_ellipse.png`,
    title: () => tc('The support [strong: of dozens of certified experts]', { strong: <strong /> }),
  },
  {
    image: `${process.env.ASSET_PREFIX}/assets/img/discover/formation_ellipse.png`,
    title: () => tc('A community composed of [strong: thousands of alumni]', { strong: <strong /> }),
  },
  {
    image: `${process.env.ASSET_PREFIX}/assets/img/discover/products_ellipse.png`,
    title: () => tc('[strong: 8,000 ethical & environmentally responsible] listed products', { strong: <strong /> }),
  },
  {
    image: `${process.env.ASSET_PREFIX}/assets/img/discover/projects_ellipse.png`,
    title: () => tc('[strong: 50,000] successfully funded projects', { strong: <strong /> }),
  },
  {
    image: `${process.env.ASSET_PREFIX}/assets/img/discover/members_ellipse.png`,
    title: () =>
      tc('And, thanks to [strong: you], a community of [strong: over 4 million members]', {
        strong: <strong />,
      }),
  },
]

const FiguresWrapper = styled.div`
  position: relative;
  min-height: 634px;
  margin: 70px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 0px 120px 20px;
  border: 4px solid ${plume.COLORS.BLUE_SHADE_1};

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    max-width: 1088px;
    margin: 120px auto;
    padding: 80px 128px;
  }

  ${plume.styles.heading.L} {
    position: relative;
  }

  ${Pop} {
    position: absolute;
    left: -20%;
    top: -50%;
    transform: scale(0.7);

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      left: -15%;
      transform: scale(1);
    }

    @media screen and ${plume.BREAKPOINTS.LAPTOP} {
      top: -50%;
    }
  }
`

const Annotation = styled.div`
  position: absolute;
  bottom: -34px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 300px;
  background-color: ${plume.COLORS.PRIMARY_WHITE};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 20px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    bottom: -40px;
    width: max-content;
    flex-direction: row;
    padding: 0 30px;

    ${plume.styles.copy.S} {
      font-weight: 600; // HACK: Irregular font manipulation
    }
  }
`

const GridFigures = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
  gap: 80px 0px;
  margin-top: 58px;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    justify-content: space-between;
    gap: 60px 0px;
  }
`

const Figure = styled.div`
  position: relative;
  display: flex;
  height: 70px;

  &:nth-child(2) {
    ${SimpleDashedLineXS} {
      transform: rotateY(180deg);
    }
  }

  &:nth-child(4) {
    ${SimpleDashedLineXS} {
      transform: rotateY(180deg);
    }
  }

  &:nth-child(5) {
    ${SimpleDashedLineXS} {
      transform: rotateY(360deg);
    }
  }

  &:nth-child(6) {
    ${SimpleDashedLine}, ${SimpleDashedLineXS} {
      display: none;
    }
  }

  ${plume.Pastille} {
    padding-left: 20px;
  }

  ${plume.styles.copy.M} {
    max-width: 199px;
    margin-left: 40px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    width: 256px;
    height: 138px;
    flex-direction: column;
    justify-content: space-between;

    ${plume.styles.copy.M} {
      max-width: 100%;
      margin-left: 0;
      padding-top: 20px;
      margin-top: 15px;
    }

    &:nth-child(2) {
      ${SimpleDashedLineXS} {
        transform: rotateY(360deg);
      }

      ${SimpleDashedLine} {
        top: 5%;
        transform: rotateY(180deg) rotateZ(180deg);
      }
    }

    &:nth-child(3) {
      ${SimpleDashedLineXS} {
        transform: rotateY(180deg);
        left: 15%;
      }
    }

    &:nth-child(4) {
      ${SimpleDashedLineXS} {
        transform: rotateY(180deg);
        left: 15%;
      }
    }

    &:nth-child(5) {
      ${SimpleDashedLineXS} {
        display: none;
      }

      ${SimpleDashedLine} {
        top: 5%;
        transform: rotateY(180deg) rotateZ(180deg);
      }
    }
  }
`

const FigureLine = styled.div`
  ${SimpleDashedLine} {
    display: none;

    @media screen and ${plume.BREAKPOINTS.LAPTOP} {
      display: block;
      position: absolute;
      right: 0;
      top: 20%;
    }
  }

  ${SimpleDashedLineXS} {
    position: absolute;
    left: 10%;
    top: 110%;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      top: 105%;
    }

    @media screen and ${plume.BREAKPOINTS.LAPTOP} {
      display: none;
    }
  }
`
